import React, { useState, useMemo } from 'react';
import { PlusIcon, FileTextIcon, MessageCircleIcon, ScaleIcon, IndianRupeeIcon, BellIcon, StickyNoteIcon, EditIcon, DropletIcon } from 'lucide-react';
import { useClientTimeline } from '../../hooks/useTimeline';
import { formatDistanceToNow } from 'date-fns';

interface TimelineTabProps {
  clientId: string;
  onNavigateToTab?: (tab: string) => void;
}

// Map database event types to UI display types
const EVENT_TYPE_MAP: Record<string, string> = {
  'payment_recorded': 'payment',
  'payment_updated': 'payment',
  'payment_deleted': 'payment',
  'measurement_recorded': 'measurement',
  'blood_parameter_recorded': 'blood_parameter',
  'blood_parameter_updated': 'blood_parameter',
  'blood_parameter_deleted': 'blood_parameter',
  'note_added': 'note',
  'note_updated': 'note',
  'note_deleted': 'note',
  'reminder_created': 'reminder',
  'reminder_completed': 'reminder',
  'reminder_cancelled': 'reminder',
  'plan_shared': 'plan',
  'client_created': 'info',
  'client_updated': 'info',
  'message_sent': 'chat',
  'message_received': 'chat',
};

export function TimelineTab({
  clientId,
  onNavigateToTab
}: TimelineTabProps) {
  const [activeFilter, setActiveFilter] = useState('all');
  const timelineQuery = useClientTimeline(clientId);

  const typeIconMap = {
    info: EditIcon,
    status: EditIcon,
    plan: FileTextIcon,
    note: StickyNoteIcon,
    reminder: BellIcon,
    measurement: ScaleIcon,
    blood_parameter: DropletIcon,
    payment: IndianRupeeIcon,
    chat: MessageCircleIcon
  };

  // Very subtle background colors - icon is primary indicator
  const typeColorMap = {
    info: '#B8C9D0',      // Very light teal
    status: '#B8C9D0',    // Very light teal
    plan: '#C7D1C3',      // Very light green-gray
    note: '#D4CBBD',      // Very light taupe
    reminder: '#DDD4C8',  // Very light warm taupe
    measurement: '#C7D1C3', // Very light green-gray
    blood_parameter: '#F5CECE', // Very light red/pink for blood
    payment: '#C1CCC7',   // Very light sage
    chat: '#E5E7EB'       // Very light gray
  };

  // Format timeline events from database
  const allEvents = useMemo(() => {
    if (!timelineQuery.data) return [];

    return timelineQuery.data.map(event => {
      const uiType = EVENT_TYPE_MAP[event.type] || 'info';
      const eventDate = new Date(event.created_at);

      // Format relative time (e.g., "3 days ago", "2 weeks ago")
      let relativeTime;
      try {
        relativeTime = formatDistanceToNow(eventDate, { addSuffix: true });
      } catch {
        relativeTime = eventDate.toLocaleDateString();
      }

      // Format time (e.g., "10:30 AM")
      const timeString = eventDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      return {
        id: event.id,
        type: uiType,
        title: event.label,
        description: event.description || '',
        date: relativeTime,
        time: timeString,
        icon: typeIconMap[uiType as keyof typeof typeIconMap] || FileTextIcon,
        color: typeColorMap[uiType as keyof typeof typeColorMap] || '#9E9E9E'
      };
    });
  }, [timelineQuery.data]);
  const filters = [{
    key: 'all',
    label: 'All'
  }, {
    key: 'plans',
    label: 'Plans'
  }, {
    key: 'notes',
    label: 'Notes'
  }, {
    key: 'reminders',
    label: 'Reminders'
  }, {
    key: 'measurement',
    label: 'Measurements'
  }, {
    key: 'blood_parameter',
    label: 'Blood Params'
  }, {
    key: 'payment',
    label: 'Payments'
  }];
  const filteredEvents = activeFilter === 'all' ? allEvents : allEvents.filter(e => e.type === activeFilter);
  const getTabForType = (type: string) => {
    const tabMap: Record<string, string> = {
      info: 'info',
      status: 'info',
      plan: 'plans',
      note: 'notes',
      reminder: 'reminders',
      measurement: 'measurements',
      blood_parameter: 'blood_parameters',
      payment: 'payments',
      chat: 'info'
    };
    return tabMap[type] || 'info';
  };

  // Loading state
  if (timelineQuery.isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
          {filters.map(filter => (
            <button
              key={filter.key}
              disabled
              className="px-4 py-2 rounded-lg whitespace-nowrap font-medium bg-gray-100 text-gray-400"
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-4xl">⏳</span>
          </div>
          <h3 className="text-lg font-semibold text-[#212121] mb-2">
            Loading timeline...
          </h3>
        </div>
      </div>
    );
  }

  // Error state
  if (timelineQuery.isError) {
    return (
      <div className="p-4 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">⚠️</span>
          </div>
          <h3 className="text-lg font-semibold text-[#212121] mb-2">
            Failed to load timeline
          </h3>
          <p className="text-gray-600 mb-4">
            There was an error loading the timeline events
          </p>
          <button
            onClick={() => timelineQuery.refetch()}
            className="px-4 py-2 bg-[#5B8E87] text-white rounded-lg font-medium hover:bg-[#20BA5A]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return <div className="p-4 space-y-4">
      <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
        {filters.map(filter => <button key={filter.key} onClick={() => setActiveFilter(filter.key)} className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition-colors ${activeFilter === filter.key ? 'bg-[#5B8E87] text-white' : 'bg-gray-100 text-gray-600'}`}>
            {filter.label}
          </button>)}
      </div>
      {filteredEvents.length === 0 ? <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">📋</span>
          </div>
          <h3 className="text-lg font-semibold text-[#212121] mb-2">
            No activity yet
          </h3>
          <p className="text-gray-600">
            Timeline will show all interactions with this client
          </p>
        </div> : <div className="space-y-4">
          {filteredEvents.map((event, index) => {
        const Icon = event.icon;
        return <div key={event.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  {/* Icon: Primary indicator with neutral color, subtle bg as helper */}
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
              backgroundColor: event.color
            }}>
                    <Icon className="w-5 h-5 text-gray-700" />
                  </div>
                  {index < filteredEvents.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-2" />}
                </div>
                <div className="flex-1 pb-6">
                  <button onClick={() => onNavigateToTab?.(getTabForType(event.type))} className="w-full bg-white rounded-2xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-shadow text-left">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-sm text-[#212121]">
                        {event.title}
                      </h4>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{event.date}</p>
                        <p className="text-xs text-gray-400">{event.time}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{event.description}</p>
                  </button>
                </div>
              </div>;
      })}
        </div>}
    </div>;
}